'use client';

import { useHomesStore } from '~/store';
import { memo, useEffect, useState, useMemo, useCallback } from 'react';
import { useParams } from 'next/navigation';
import { useFilters } from '~/hooks/useFilters';
import Maintenance from '~/components/utility/maintenance/page';
import {
  Area,
  Broker,
  LocationIQReverseLocation,
  NestedSpeculator,
  NestedHome,
  NestedSearch,
  NestedUser,
  SearchTab,
  TODO,
} from '@habity/types';
import { BrokersGrid } from '../../ui/grids/BrokersGrid';
import { HomesGrid } from '../../ui/grids/HomesGrid';
import { getAPI, getAuthAPI } from '~/lib/api';
import { SearchMap } from '../../map/SearchMap';
import type { MapHomeLocation } from '@habity/types';
import useSWR, { preload } from 'swr';
import useSWRInfinite from 'swr/infinite';
import { get, getTableKey } from '~/lib/providers/supabase/swr';
import { useStableSearchParams } from '~/hooks/useStableSearchParams';
import { SpeculatorsGrid } from './components/SpeculatorsGrid';
import { useUser } from '~/hooks/useUser';
import { AreasGrid } from '~/components/ui/grids/AreasGrid';
import { MunicipalitiesGrid } from '~/components/ui/grids/MunicipalitiesGrid';
import { InfiniteScrollTrigger } from '~/components/ui/scroll/InfiniteScrollTrigger';
import { PageContent } from '~/components/ui/page/content';
import { PlacesList } from '~/components/ui/grids/PlacesList';
import { ProjectsGrid } from '~/components/ui/grids/ProjectsGrid';
import {
  generatePromotionalHomeIndexes,
  interlacePromotionalHomes,
} from '~/lib/homes/list/promotional';

const INFINITE_SCROLL = true;

type HomesListProps = {
  error: TODO;
  filters: Partial<NestedSearch>;
  locations: LocationIQReverseLocation[];
  data?: NestedHome[] | Broker[] | NestedSpeculator[] | Area[];
  promotionalHomes: NestedHome[];
  homeLocations: MapHomeLocation[] | null;
  tab: SearchTab;
  map: boolean;
};

const CardListComponent = ({
  error,
  filters,
  homeLocations,
  data: initialData = [],
  promotionalHomes = [],
  tab,
  map,
}: HomesListProps) => {
  const { applyFiltersFromQuery } = useFilters();
  const { searchId, addressId } = useParams() || {};
  const searchParams = useStableSearchParams();
  const { user: appUser } = useUser();

  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // FIXME: Decouple count from useHomesStore
  // They're not home-specific only. All search pages have counts
  const count = useHomesStore((state) => state.count);
  const setCount = useHomesStore((state) => state.setCount);

  const promotionalHomeStableIndexes = useMemo(
    () => generatePromotionalHomeIndexes(promotionalHomes.length),
    [promotionalHomes.length]
  );

  useEffect(() => {
    if (appUser?.id) {
      preload(['users', appUser.id], ([table, userId]) => get(table, userId));
    }
  }, [appUser?.id]);

  const { data: user } = useSWR(
    appUser?.id ? ['users', appUser.id] : null,
    ([table, userId]): Promise<NestedUser> => get(table, userId),
    { revalidateOnFocus: false }
  );

  // Stable key generator for SWR
  // Note: showBuyerDetails only matters for speculators tab and is checked server-side anyway
  const getKey = useCallback(
    (page: number, previousPageData: TODO[][]) =>
      getTableKey({
        page,
        previousPageData,
        tab,
        searchParams,
        searchId,
        addressId,
        authUserId: appUser?.id?.toString(),
        showBuyerDetails: false, // Server checks auth independently
        limit: undefined,
      }),
    [tab, searchParams, searchId, addressId, appUser?.id]
  );

  // Speculators fetching requires authentication
  const getAPIFn = tab === 'speculators' ? getAuthAPI : getAPI;

  const tabData = useSWRInfinite<TODO[]>(getKey, getAPIFn, {
    fallbackData: initialData?.length > 0 ? [initialData] : undefined,
    revalidateOnFocus: false,
    revalidateFirstPage: false,
    suspense: false,
    dedupingInterval: 5000,
  });

  const data = tabData?.data;

  const { isLoading: dataLoading, isValidating: dataValidating } = tabData;
  const [firstLoadComplete, setFirstLoadComplete] = useState(
    (initialData?.length ?? 0) > 0
  );

  useEffect(() => {
    if (!dataLoading && !dataValidating) {
      setIsLoadingMore(false);
      if (!firstLoadComplete) {
        setFirstLoadComplete(true);
      }
    }
  }, [dataLoading, dataValidating, firstLoadComplete]);

  useEffect(() => {
    // Only update count when data has finished loading
    if (dataLoading || dataValidating) return;

    // When results are empty, filteredData is [[]] so we need to check the array length
    const firstPageData = data?.[0] ?? [];
    const newCount =
      firstPageData.length > 0 ? firstPageData[0]?.count ?? 0 : 0;
    const newUniqueCount =
      tab === 'speculators'
        ? data?.[0]?.[0]?.user_count ?? 0
        : count.speculatorsUnique;
    const newViewingCount =
      tab === 'speculators'
        ? data?.[0]?.[0]?.viewing_count ?? 0
        : count.speculatorsViewing;
    const newBiddingCount =
      tab === 'speculators'
        ? data?.[0]?.[0]?.bid_count ?? 0
        : count.speculatorsBidding;

    const tabKey = tab as keyof typeof count;
    const countChanged = count[tabKey] !== newCount;
    const uniqueCountChanged =
      tab === 'speculators' && count.speculatorsUnique !== newUniqueCount;
    const viewingCountChanged =
      tab === 'speculators' && count.speculatorsViewing !== newViewingCount;
    const biddingCountChanged =
      tab === 'speculators' && count.speculatorsBidding !== newBiddingCount;

    if (
      countChanged ||
      uniqueCountChanged ||
      viewingCountChanged ||
      biddingCountChanged
    ) {
      setCount({
        ...count,
        [tabKey]: newCount,
        speculatorsUnique: newUniqueCount,
        speculatorsViewing: newViewingCount,
        speculatorsBidding: newBiddingCount,
      });
    }
  }, [data, tab, count, setCount, dataLoading, dataValidating]);

  useEffect(() => {
    applyFiltersFromQuery(filters);
  }, []);

  const handleShowMore = useCallback(() => {
    setIsLoadingMore(true);
    tabData.setSize(tabData.size + 1);
  }, [tabData]);

  const isInitialLoading =
    (initialData?.length ?? 0) === 0 &&
    !firstLoadComplete &&
    (dataLoading || dataValidating);

  // Grid items are stable with respect to certain parameters so memoize against those
  const gridItems = useMemo(() => {
    const pageSize = ['homes', 'speculators'].includes(tab)
      ? 12
      : 6;
    const skeletons = Array.from({ length: pageSize }, (_, i) => ({
      loading: true,
      id: `skeleton-${i}`,
    }));

    let items = data?.flatMap((item) => item) ?? [];
    const itemCount = items?.length ?? 0;
    const initialCount = initialData?.length ?? 0;

    if (itemCount === 0 && initialCount > 0) return initialData;
    if (isInitialLoading) return skeletons;

    if (tab === 'homes') {
      items = interlacePromotionalHomes(
        items as NestedHome[],
        promotionalHomes,
        promotionalHomeStableIndexes
      );
    }

    if (isLoadingMore) return [...items, ...skeletons];
    return items;
  }, [
    data,
    initialData,
    isInitialLoading,
    tab,
    promotionalHomes,
    promotionalHomeStableIndexes,
    isLoadingMore,
  ]);

  const flattenedDataLength = data?.flatMap((item) => item)?.length ?? 0;
  const currentTabCount = count[tab as keyof typeof count] ?? 0;
  const hasMore = flattenedDataLength < currentTabCount;

  if (error) return <Maintenance />;

  const TabContent = () => {
    if (tab === 'homes') {
      if (map) return <SearchMap homes={homeLocations} />;
      return (
        <HomesGrid
          user={user}
          isLoggedIn={!!appUser}
          homes={gridItems}
          homesLoading={isInitialLoading}
          hasMore={hasMore}
          isLoadingMore={isLoadingMore}
        />
      );
    }

    if (tab === 'speculators') {
      return (
        <SpeculatorsGrid
          speculators={gridItems}
          loading={isInitialLoading}
          hasMore={hasMore}
        />
      );
    }

    if (tab === 'brokers') {
      return (
        <BrokersGrid
          brokers={gridItems}
          brokersLoading={isInitialLoading}
          hasMore={hasMore}
        />
      );
    }

    if (tab === 'areas') {
      return <AreasGrid areas={gridItems} areasLoading={isInitialLoading} />;
    }

    if (tab === 'municipalities') {
      return (
        <MunicipalitiesGrid
          municipalities={gridItems}
          isLoading={isInitialLoading}
        />
      );
    }

    if (tab === 'places') {
      return <PlacesList places={gridItems} isLoading={isInitialLoading} />;
    }

    if (tab === 'projects') {
      return (
        <ProjectsGrid
          projects={gridItems}
          projectsLoading={isInitialLoading}
          hasMore={hasMore}
        />
      );
    }

    return null;
  };

  return (
    <PageContent containerProps={{ py: 0 }} mt="3">
      <TabContent />
      {!map && (
        <InfiniteScrollTrigger
          onVisible={handleShowMore}
          isLoading={isLoadingMore || dataLoading || dataValidating}
          hasMore={hasMore}
          infiniteScrollEnabled={INFINITE_SCROLL}
          results={flattenedDataLength}
          total={currentTabCount}
          showLoader={false} // We don't show spinners, we show context-appropriate skeletons
          buttonText="Ladda fler"
          boxProps={{
            mt: '4',
            mb: { mobile: '24', desktop: '8' },
          }}
        />
      )}
    </PageContent>
  );
};

export const CardList = memo(CardListComponent);
